import { MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS } from "@/lib/data/static/matterStatics";
import type { MedicalTreatmentBill } from "@/lib/types";
import { z } from "zod";

export type InlineTreatmentBillFieldErrorKey =
  | "recordLabel"
  | "recordStatus"
  | "recordRequestDate"
  | "recordReceivedDate"
  | "recordRejectedDate"
  | "originalBill";

export type InlineTreatmentBillFieldErrors = Partial<
  Record<InlineTreatmentBillFieldErrorKey, string>
>;

export type InlineTreatmentBillFormValues = Partial<{
  recordLabel: string | null;
  recordStatus: string;
  recordRequestDate: string | null;
  recordReceivedDate: string | null;
  recordRejectedDate: string | null;
  originalBill: string | number | null;
}>;

const INLINE_TREATMENT_BILL_FIELD_ERROR_KEYS =
  new Set<InlineTreatmentBillFieldErrorKey>([
    "recordLabel",
    "recordStatus",
    "recordRequestDate",
    "recordReceivedDate",
    "recordRejectedDate",
    "originalBill",
  ]);

const isInlineTreatmentBillFieldErrorKey = (
  field: string,
): field is InlineTreatmentBillFieldErrorKey =>
  INLINE_TREATMENT_BILL_FIELD_ERROR_KEYS.has(
    field as InlineTreatmentBillFieldErrorKey,
  );

const inlineTreatmentBillValidationSchema = z
  .object({
    recordLabel: z.string().trim().max(50, {
      message: "Record name must be less than 50 characters",
    }),
    recordStatus: z
      .enum(MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS)
      .or(z.literal(""))
      .refine((value) => value, { message: "Status is required" }),
    recordRequestDate: z.string().nullable().optional(),
    recordReceivedDate: z.string().nullable().optional(),
    recordRejectedDate: z.string().nullable().optional(),
    originalBill: z.coerce
      .number()
      .max(1000000000, {
        message: "Original bill must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null)),
  })
  .superRefine((data, ctx) => {
    if (["Requested", "Received"].includes(data.recordStatus)) {
      if (!data.recordRequestDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Request date is required",
          path: ["recordRequestDate"],
        });
      }
    }
    if (data.recordStatus === "Received") {
      if (!data.recordReceivedDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Date received is required",
          path: ["recordReceivedDate"],
        });
      }
    }
    if (data.recordStatus === "Rejected") {
      if (!data.recordRejectedDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Rejected date is required",
          path: ["recordRejectedDate"],
        });
      }
    }
  });

export class InlineTreatmentBillValidationError extends Error {
  errors: InlineTreatmentBillFieldErrors;

  constructor(errors: InlineTreatmentBillFieldErrors) {
    super("Inline treatment bill validation failed");
    this.name = "InlineTreatmentBillValidationError";
    this.errors = errors;
  }
}

export const normalizeInlineTreatmentBillFormValues = (
  values: InlineTreatmentBillFormValues,
) => ({
  recordLabel: values.recordLabel ?? "",
  recordStatus: values.recordStatus ?? "",
  recordRequestDate: values.recordRequestDate || null,
  recordReceivedDate: values.recordReceivedDate || null,
  recordRejectedDate: values.recordRejectedDate || null,
  originalBill:
    values.originalBill === "" || values.originalBill === undefined
      ? null
      : values.originalBill,
});

export const validateInlineTreatmentBillForm = (
  values: InlineTreatmentBillFormValues,
): InlineTreatmentBillFieldErrors => {
  const result = inlineTreatmentBillValidationSchema.safeParse(
    normalizeInlineTreatmentBillFormValues(values),
  );

  if (result.success) {
    return {};
  }

  const errors: InlineTreatmentBillFieldErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0];
    if (
      typeof field === "string" &&
      isInlineTreatmentBillFieldErrorKey(field) &&
      !errors[field]
    ) {
      errors[field] = issue.message;
    }
  }

  if (Object.keys(errors).length === 0 && result.error.issues[0]) {
    errors.recordLabel = result.error.issues[0].message;
  }

  return errors;
};

export const assertInlineTreatmentBillFormValid = (
  values: InlineTreatmentBillFormValues,
) => {
  const errors = validateInlineTreatmentBillForm(values);
  if (Object.keys(errors).length > 0) {
    throw new InlineTreatmentBillValidationError(errors);
  }
};

const BILL_RECORD_STATUSES_WITHOUT_LIEN = new Set([
  "Not Requested",
  "Not yet requested",
  "N/A",
]);

export const canSetMedicalTreatmentBillLien = (
  recordStatus: string | undefined,
) =>
  Boolean(recordStatus && !BILL_RECORD_STATUSES_WITHOUT_LIEN.has(recordStatus));

export const normalizeBillLienForRecordStatus = <
  T extends {
    recordStatus?: string;
    lien?: string;
  },
>(
  bill: T,
  recordStatus?: string,
): T => {
  const status = recordStatus ?? bill.recordStatus;
  if (canSetMedicalTreatmentBillLien(status)) {
    return bill;
  }
  return { ...bill, lien: "" };
};

export const calculateMedicalTreatmentBillTotals = (
  medicalTreatmentBills: Pick<
    MedicalTreatmentBill,
    | "originalBill"
    | "negotiatedAmount"
    | "adjustedBill"
    | "insurancePaid"
    | "medpayPipPaid"
    | "clientPaid"
  >[],
) => {
  const totalBill = medicalTreatmentBills.reduce(
    (acc, medicalTreatmentBill) =>
      acc +
      Number(
        medicalTreatmentBill.negotiatedAmount ||
          medicalTreatmentBill.adjustedBill ||
          medicalTreatmentBill.originalBill ||
          0,
      ),
    0,
  );
  const totalMedpayPaid = medicalTreatmentBills.reduce(
    (acc, medicalTreatmentBill) =>
      acc + Number(medicalTreatmentBill.medpayPipPaid || 0),
    0,
  );
  const totalInsurancePaid = medicalTreatmentBills.reduce(
    (acc, medicalTreatmentBill) =>
      acc + Number(medicalTreatmentBill.insurancePaid || 0),
    0,
  );
  const totalClientPaid = medicalTreatmentBills.reduce(
    (acc, medicalTreatmentBill) =>
      acc + Number(medicalTreatmentBill.clientPaid || 0),
    0,
  );
  const totalPaid = medicalTreatmentBills.reduce(
    (acc, medicalTreatmentBill) =>
      acc +
      Number(medicalTreatmentBill.medpayPipPaid || 0) +
      Number(medicalTreatmentBill.insurancePaid || 0) +
      Number(medicalTreatmentBill.clientPaid || 0),
    0,
  );
  const totalOutstanding = totalBill - totalPaid;
  return {
    totalBill,
    totalPaid,
    totalOutstanding,
    totalMedpayPaid,
    totalInsurancePaid,
    totalClientPaid,
  };
};
